import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, ArrowLeft, Loader2 } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { receiptReturnService, formatInvoiceNo } from '@/services/receiptReturnService'
import { financeService } from '@/services/financeService'
import { receiptsService } from '@/services/receiptsService'
import {
  ReceiptReturnVm,
  ReceiptReturnInvoice,
  ReceiptReturnInsertData,
  FinanceCustomer,
  ReceiptAccount,
} from '@/api/types'
import { calendarToday, calendarFromApi, calendarToApi } from '@/lib/calendarDate'

export const ReceiptReturnsDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const isViewMode = id !== 'new' && id !== undefined
  const isReadOnly = isViewMode
  const branchId = user?.selectedBranch?.branchId

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loadingInsertData, setLoadingInsertData] = useState(false)

  // Form state
  const [formData, setFormData] = useState<{
    customerId: number
    invoiceId: number
    destinationAccount: number
    amount: number
    date: string
    note: string
    returnType: string
    returnNo: string
    invoiceNo: string
  }>({
    customerId: 0,
    invoiceId: 0,
    destinationAccount: 0,
    amount: 0,
    date: calendarToday(),
    note: '',
    returnType: 'Cash',
    returnNo: '',
    invoiceNo: '',
  })

  // Lookup data
  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [customerInvoices, setCustomerInvoices] = useState<ReceiptReturnInvoice[]>([])
  const [accounts, setAccounts] = useState<ReceiptAccount[]>([])
  const [insertData, setInsertData] = useState<ReceiptReturnInsertData | null>(null)

  const returnTypeOptions = useMemo(
    () => [
      { value: 'Cash', label: t('receiptReturns.cash') },
      { value: 'Bank', label: t('receiptReturns.bank') },
    ],
    [t]
  )

  // Load customers and accounts
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        setLoading(true)
        const [customersData, accountsData] = await Promise.all([
          financeService.getAllCustomers(branchId),
          receiptsService.getAllAccounts(branchId),
        ])

        setCustomers(customersData || [])
        setAccounts(accountsData || [])
      } catch (error) {
        console.error('Error loading lookups:', error)
        showSaveError(error as Error, false)
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId])

  // Load invoices when customer is selected
  useEffect(() => {
    if (!formData.customerId || formData.customerId === 0) {
      setCustomerInvoices([])
      if (!isViewMode) {
        setFormData(prev => ({ ...prev, invoiceId: 0 }))
      }
      return
    }

    const loadInvoices = async () => {
      try {
        setLoading(true)
        const invoices = await receiptReturnService.getInvoicesByCustomerId(formData.customerId)
        setCustomerInvoices(invoices || [])
        if (!isViewMode) {
          setFormData(prev => ({ ...prev, invoiceId: 0 }))
          setInsertData(null)
        }
      } catch (error) {
        console.error('Error loading invoices:', error)
        await showValidationError(t('receiptReturns.failedToLoadInvoices'))
      } finally {
        setLoading(false)
      }
    }

    loadInvoices()
  }, [formData.customerId, isViewMode])

  // Auto-fill form when invoice is selected (create only)
  useEffect(() => {
    if (isViewMode) return
    if (!formData.invoiceId || formData.invoiceId === 0 || !branchId) {
      setInsertData(null)
      setFormData(prev => ({
        ...prev,
        destinationAccount: 0,
        amount: 0,
      }))
      return
    }

    const loadInsertData = async () => {
      try {
        setLoadingInsertData(true)
        const data = await receiptReturnService.getInsertData(branchId, formData.invoiceId)
        setInsertData(data)

        // Auto-fill form with insert data
        if (data && data.receiptReturn) {
          setFormData(prev => ({
            ...prev,
            destinationAccount: data.receiptReturn.destinationAccount || 0,
            amount: data.receiptReturn.amount || 0,
            date: data.receiptReturn.date ? calendarFromApi(data.receiptReturn.date) || prev.date : prev.date,
            note: data.receiptReturn.note || '',
            returnType: data.receiptReturn.returnType || 'Cash',
          }))
        }
      } catch (error) {
        console.error('Error loading insert data:', error)
        await showValidationError(t('receiptReturns.failedToLoadReceiptData'))
      } finally {
        setLoadingInsertData(false)
      }
    }

    loadInsertData()
  }, [formData.invoiceId, branchId, isViewMode])

  // Load existing receipt return for view mode
  useEffect(() => {
    if (!isViewMode || !id || !branchId) return

    const loadReceiptReturn = async () => {
      try {
        setLoading(true)
        const data = await receiptReturnService.getById(Number(id))
        const dateValue = data.date ? calendarFromApi(data.date) || calendarToday() : calendarToday()
        setFormData({
          customerId: data.customerId || 0,
          invoiceId: data.invoiceId || 0,
          destinationAccount: data.destinationAccount || 0,
          amount: data.amount || 0,
          date: dateValue,
          note: data.note || '',
          returnType: data.returnType || 'Cash',
          returnNo: data.returnNo || '',
          invoiceNo: formatInvoiceNo(data.invoiceNo),
        })
      } catch (error: any) {
        console.error('Error loading receipt return:', error)
        await showSaveError(error, false)
        navigate('/receipt-returns')
      } finally {
        setLoading(false)
      }
    }

    loadReceiptReturn()
  }, [isViewMode, id, branchId, navigate])

  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (isReadOnly) {
      await showValidationError(t('receiptReturns.cannotUpdate') || 'Receipt return cannot be updated.')
      return
    }

    if (!branchId) {
      await showValidationError(t('receiptReturns.pleaseSelectBranch'))
      return
    }

    if (!formData.customerId || formData.customerId === 0) {
      await showValidationError(t('receiptReturns.pleaseSelectCustomer'))
      return
    }

    if (!formData.invoiceId || formData.invoiceId === 0) {
      await showValidationError(t('receiptReturns.pleaseSelectInvoice'))
      return
    }

    if (!formData.destinationAccount || formData.destinationAccount === 0) {
      await showValidationError(t('receiptReturns.pleaseSelectDestinationAccount'))
      return
    }

    if (!formData.amount || formData.amount <= 0) {
      await showValidationError(t('receiptReturns.pleaseEnterValidAmount'))
      return
    }

    if (!formData.date) {
      await showValidationError(t('receiptReturns.pleaseSelectDate'))
      return
    }

    if (!formData.returnType) {
      await showValidationError(t('receiptReturns.pleaseSelectReturnType'))
      return
    }

    if (insertData && formData.amount > insertData.maxReturnAmount) {
      await showValidationError(
        t('receiptReturns.amountExceedsMax', { max: insertData.maxReturnAmount.toFixed(2) })
      )
      return
    }

    setSaving(true)

    try {
      const ipAddress = await getUserIPAddress()

      const saveData: ReceiptReturnVm = {
        id: 0,
        invoiceId: formData.invoiceId,
        customerId: formData.customerId,
        destinationAccount: formData.destinationAccount,
        amount: formData.amount,
        date: calendarToApi(formData.date),
        note: formData.note,
        returnType: formData.returnType,
        ipAddress: ipAddress,
      }

      await receiptReturnService.saveReceiptReturn(saveData)
      await showSaveSuccess(false, t('receiptReturns.receiptReturnSavedSuccess'))
      navigate('/receipt-returns')
    } catch (error: any) {
      console.error('Error saving receipt return:', error)
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        t('receiptReturns.failedToSaveReceiptReturn')
      await showSaveError(new Error(errorMessage), false)
    } finally {
      setSaving(false)
    }
  }

  // Prepare options for dropdowns
  const customerOptions: SearchableSelectOption[] = customers.map((cust) => ({
    value: String(cust.customerId || 0),
    label: cust.customerNameEn || cust.customerNameAr || `Customer ${cust.customerId}`,
  }))
  if (isViewMode && formData.customerId && !customerOptions.some((opt) => opt.value === String(formData.customerId))) {
    customerOptions.push({
      value: String(formData.customerId),
      label: `Customer ${formData.customerId}`,
    })
  }

  const invoiceOptions: SearchableSelectOption[] = customerInvoices.map((inv) => ({
    value: String(inv.invoiceId || inv.InvoiceId || 0),
    label: formatInvoiceNo(inv.invoiceNo || inv.invoiceNumber || inv.InvoiceNo) || `Invoice ${inv.invoiceId || inv.InvoiceId}`,
  }))
  if (isViewMode && formData.invoiceId && !invoiceOptions.some((opt) => opt.value === String(formData.invoiceId))) {
    invoiceOptions.push({
      value: String(formData.invoiceId),
      label: formatInvoiceNo(formData.invoiceNo) || formatInvoiceNo(formData.invoiceId),
    })
  }

  const accountOptions: SearchableSelectOption[] = accounts.map((acc) => {
    const accountId = acc.accountId || acc.id || 0
    const accountName = acc.accountNameEn || acc.accountName || acc.accountNameAr || ''
    const accountCode = acc.accountCode || ''
    const displayLabel = accountCode ? `${accountCode} - ${accountName}` : accountName
    return {
      value: String(accountId),
      label: displayLabel,
    }
  })
  if (isViewMode && formData.destinationAccount && !accountOptions.some((opt) => opt.value === String(formData.destinationAccount))) {
    accountOptions.push({
      value: String(formData.destinationAccount),
      label: String(formData.destinationAccount),
    })
  }

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  if (loading && !formData.customerId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('receiptReturns.title')}
        description={isViewMode ? (t('receiptReturns.viewReceiptReturn') || 'View receipt return') : t('receiptReturns.createNewReceiptReturn')}
      >
        <Button variant="outline" onClick={() => navigate('/receipt-returns')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('receiptReturns.backToList')}
        </Button>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>{t('receiptReturns.receiptReturnInformation')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isViewMode && formData.returnNo && (
            <div className="space-y-2">
              <Label className="text-gray-700 dark:text-gray-300 font-medium">
                {t('receiptReturns.returnNo')}
              </Label>
              <Input value={formData.returnNo} readOnly className="bg-muted" />
            </div>
          )}

          {/* Customer and Invoice Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Customer Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="customer" className="text-gray-700 dark:text-gray-300 font-medium">
                {t('receiptReturns.customer')} <span className="text-destructive">*</span>
              </Label>
              <SearchableSelect
                options={customerOptions}
                value={String(formData.customerId)}
                onValueChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    customerId: Number(value),
                    invoiceId: 0, // Reset invoice when customer changes
                  }))
                }}
                placeholder={t('receiptReturns.selectCustomer')}
                searchPlaceholder={t('receiptReturns.searchCustomer')}
                emptyMessage={t('receiptReturns.noCustomerFound')}
                disabled={isReadOnly || saving || loading}
              />
            </div>

            {/* Invoice Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="invoice" className="text-gray-700 dark:text-gray-300 font-medium">
                {t('receiptReturns.invoice')} <span className="text-destructive">*</span>
              </Label>
              <SearchableSelect
                options={invoiceOptions}
                value={String(formData.invoiceId)}
                onValueChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    invoiceId: Number(value),
                  }))
                }}
                placeholder={
                  formData.customerId ? t('receiptReturns.selectInvoice') : t('receiptReturns.pleaseSelectCustomerFirst')
                }
                searchPlaceholder={t('receiptReturns.searchInvoice')}
                emptyMessage={
                  formData.customerId ? t('receiptReturns.noInvoicesFound') : t('receiptReturns.pleaseSelectCustomerFirst')
                }
                disabled={isReadOnly || saving || loading || !formData.customerId || (!isViewMode && customerInvoices.length === 0)}
              />
              {loadingInsertData && formData.invoiceId > 0 && (
                <p className="text-sm text-muted-foreground">{t('receiptReturns.loadingReceiptData')}</p>
              )}
            </div>
          </div>

          {/* Other Fields in Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Destination Account Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="destinationAccount" className="text-gray-700 dark:text-gray-300 font-medium">
                {t('receiptReturns.creditAccount') || t('receiptReturns.destinationAccount')} <span className="text-destructive">*</span>
              </Label>
              <SearchableSelect
                options={accountOptions}
                value={String(formData.destinationAccount)}
                onValueChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    destinationAccount: Number(value),
                  }))
                }}
                placeholder={t('receiptReturns.selectDestinationAccount')}
                searchPlaceholder={t('receiptReturns.searchAccount')}
                emptyMessage={t('receiptReturns.noAccountFound')}
                disabled
              />
              <p className="text-sm text-muted-foreground">
                {t('receiptReturns.copiedFromReceiptDebit') || 'Taken from the original receipt debit account.'}
              </p>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount" className="text-gray-700 dark:text-gray-300 font-medium">
                {t('receiptReturns.amount')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    amount: Number(e.target.value),
                  }))
                }}
                disabled={isReadOnly || saving || loading}
                max={insertData?.maxReturnAmount}
              />
              {insertData && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('receiptReturns.maxReturnAmount', { amount: formatAmount(insertData.maxReturnAmount) })}
                </p>
              )}
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label htmlFor="date" className="text-gray-700 dark:text-gray-300 font-medium">
                {t('receiptReturns.date')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    date: e.target.value,
                  }))
                }}
                disabled={isReadOnly || saving || loading}
              />
            </div>

            {/* Return Type */}
            <div className="space-y-2">
              <Label htmlFor="returnType" className="text-gray-700 dark:text-gray-300 font-medium">
                {t('receiptReturns.returnType')} <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.returnType}
                onValueChange={(value) => {
                  setFormData(prev => ({
                    ...prev,
                    returnType: value,
                  }))
                }}
                disabled
              >
                <SelectTrigger id="returnType" className="bg-muted">
                  <SelectValue placeholder={t('receiptReturns.selectReturnType')} />
                </SelectTrigger>
                <SelectContent>
                  {returnTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {t('receiptReturns.copiedFromReceiptType') || 'Taken from the original receipt payment type.'}
              </p>
            </div>
          </div>

          {/* Note - Full Width */}
          <div className="space-y-2">
            <Label htmlFor="note" className="text-gray-700 dark:text-gray-300 font-medium">
              {t('receiptReturns.note')}
            </Label>
            <Textarea
              id="note"
              value={formData.note}
              onChange={(e) => {
                setFormData(prev => ({
                  ...prev,
                  note: e.target.value,
                }))
              }}
              disabled={isReadOnly || saving || loading}
              placeholder={t('receiptReturns.enterNote')}
              rows={3}
            />
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/receipt-returns')}
              className="w-full sm:w-auto"
              disabled={saving}
            >
              {isViewMode ? t('receiptReturns.backToList') : t('common.cancel')}
            </Button>
            {!isReadOnly && (
              <Button
                type="button"
                onClick={handleSave}
                className="w-full sm:w-auto"
                disabled={saving || loading}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('receiptReturns.saving')}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {t('receiptReturns.saveReceiptReturn')}
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

